import Docxtemplater from 'docxtemplater'
import JSZip from 'jszip' // stick with v2 otherwise will run into dependency error
import { htmlRegex } from '../constants/global'
import { Citation } from '../types/redux-citation-api'
import xmlescape from './xmlEscape'
import { publicUrl } from './url'

export const worksCitedHeaders = {
  mla9: 'Works Cited',
  mla8: 'Works Cited',
  mla7: 'Works Cited',
  apa: 'References',
  chicago: 'Bibliography',
  turabian: 'Bibliography',
  harvard: 'References',
  apa7: 'References',
}

export const getHeader = (style: string) =>
  worksCitedHeaders[style.toLowerCase()] || 'Bibliography'

const preserveSpace = (content: string) => `<w:t xml:space="preserve">${content}</w:t>`

export const cleanText = (text: string): string => text.replace(htmlRegex, '')

export const formatHtml = (string: string, citation: Citation) => {
  let str =
    '<w:r><w:rPr><w:rFonts w:ascii="Times New Roman" w:hAnsi="Times New Roman" w:cs="Times New Roman"/>'

  if (
    citation.annotation &&
    citation.annotation.text &&
    cleanText(string) === xmlescape(citation.annotation.text)
  ) {
    return `</w:p><w:p><w:pPr><w:ind w:left="720"/></w:pPr>${str}</w:rPr>${preserveSpace(
      xmlescape(citation.annotation.text)
    )}</w:r>`
  }

  // handle the weird Bluebook case with the small caps span
  // formatted output in that case looks something like: "Test, <span style="font-variant: small-caps">Wikipedia</span> (2018), https://en.wikipedia.org/wiki/Test (last visited Dec 18, 2018)."
  if (string.includes('<span style="font-variant: small-caps">')) {
    str += `<w:smallCaps w:val="true"/></w:rPr>${preserveSpace(cleanText(string))}</w:r>`
    return str
  }

  switch (string.slice(0, 3)) {
    case '<i>':
      str += `<w:i/></w:rPr>${preserveSpace(cleanText(string))}</w:r>`
      break
    case '<b>':
      str += `<w:b/></w:rPr>${preserveSpace(cleanText(string))}</w:r>`
      break
    case '<u>':
      str += `<w:u w:val="single"/></w:rPr>${preserveSpace(cleanText(string))}</w:r>`
      break
    // handle divs and spans
    case '<di':
    case '<sp':
      str += `</w:rPr>${preserveSpace(cleanText(string))}</w:r>`
      break
    // in case backend returns it's own new line we want to ignore it since we handle spacing explicitly for now
    case '<br':
      return ''
    default:
      str += `</w:rPr>${preserveSpace(xmlescape(string))}</w:r>`
  }
  return str
}

export const xmlFromString = (str) => {
  // Create a new DOMParser object
  const parser = new DOMParser()

  // Create xml representation based on html string
  const xmlDoc = parser.parseFromString(`<body>${str}</body>`, 'text/html')

  const { childNodes } = xmlDoc.querySelector('body')

  // return child nodes
  return childNodes
}

export const arrFromNodes = (nodes) => {
  // Convert NodeList to Array: https://davidwalsh.name/nodelist-array
  const nodesArray = [].slice.call(nodes)

  // You may want to silence any errors with the output... we can't really trust
  // the data we're parsing, so we'll either want to gracefully fail here,
  // or specifically throw an error saying their citation isnt' formatted correctly
  const parsed = nodesArray.filter((item) => item.nodeName !== 'parsererror')

  // Grab html from the node if it's present, otherwise grab the data
  return parsed.map((item) => item.outerHTML || item.data)
}

export const convertNsbpToSpace = (arr) => {
  return arr.map((item) => item.replace(/&nbsp;/g, ' '))
}

export const format = (citation: Citation, style?: string) => {
  const str = citation.formatted_data.output
  const line = style === 'chicago' ? 240 : 480

  const nodes = xmlFromString(str)
  const arr = arrFromNodes(nodes)
  const filterStringArr = convertNsbpToSpace(arr)

  let xmlString = `<w:p><w:pPr><w:ind w:left="720" w:hanging="720" /><w:spacing w:line="${line}" w:before="0" w:after="0" w:beforeAutospacing="0" w:afterAutospacing="0" w:line-rule="auto" /></w:pPr>`
  filterStringArr.forEach((el) => {
    xmlString += formatHtml(el, citation)
  })
  return `${xmlString}</w:p>`
}

export const createDocX = async (style: string, citations: Citation[]) => {
  // using require because window is undefined in Server
  // eslint-disable-next-line
  const JSZipUtils = require('jszip-utils')
  const xmlCitations = []
  citations.forEach((citation) => {
    xmlCitations.push({ rawXml: format(citation, style) })
  })
  return new Promise((resolve, reject) => {
    JSZipUtils.getBinaryContent(
      publicUrl('/static/tag-example.docx'),
      (error, content) => {
        if (error) {
          reject(error)
        }
        const zip = new JSZip(content)
        const doc = new Docxtemplater().loadZip(zip).setOptions({ paragraphLoop: true })
        doc.setData({
          bibliography_title: getHeader(style),
          citations: xmlCitations,
        })
        try {
          doc.render()
        } catch (error) {
          // console.error(JSON.stringify({ error }))
          reject(error)
        }
        const out = doc.getZip().generate({
          type: 'blob',
          mimeType:
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        }) // Output the document using Data-URI
        resolve(out)
      }
    )
  })
}
